import { useContext } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import Toggle from "../../Components/Input/Toggle";
import { LangContext, LangContextType } from "../../utilities/customHooks";
import { queryKeys } from "../../utilities/queryKeys";
import { getOrgEmailSettings } from "../../services/admin/orgEmailSettingsService";
import { useUpdateOrgEmailSettings } from "../../services/admin/regionMutations";
import textData from "../../textData.json";

type OrgEmailTextKey = keyof typeof textData.Pages.Settings.OrgEmailNotifications;

type ToggleGroup = {
  headingKey: OrgEmailTextKey;
  /** Group only shown to full-portal orgs (SoW/invoice/IPE workflows). */
  requiresFullPortal?: boolean;
  prefs: {
    key: string;
    labelKey: OrgEmailTextKey;
    descKey: OrgEmailTextKey;
    /** Toggle only shown to orgs whose program model has a Growth Coach step. */
    requiresGrowthCoaches?: boolean;
  }[];
};

// Keys mirror ORG_SUPPRESSIBLE_EMAILS (eip-shared/types/orgEmailPrefs.ts) —
// the server rejects anything outside that registry, so this list is the UI
// projection of the same curated set, ordered for reading.
const TOGGLE_GROUPS: ToggleGroup[] = [
  {
    headingKey: "GroupApplication",
    prefs: [
      { key: "APPLICATION_RECEIVED", labelKey: "ApplicationReceived", descKey: "ApplicationReceivedDesc" },
      { key: "APP_REQUIRES_MODIFICATION", labelKey: "AppRequiresModification", descKey: "AppRequiresModificationDesc" },
      { key: "APP_NOT_APPROVED", labelKey: "AppNotApproved", descKey: "AppNotApprovedDesc" },
      { key: "GC_APPLICATION_UPDATE", labelKey: "GcApplicationUpdate", descKey: "GcApplicationUpdateDesc", requiresGrowthCoaches: true },
    ],
  },
  {
    headingKey: "GroupAdminBroadcast",
    prefs: [
      { key: "ADMIN_NEW_APPLICATION", labelKey: "AdminNewApplication", descKey: "AdminNewApplicationDesc" },
      { key: "ADMIN_REVISED_APPLICATION", labelKey: "AdminRevisedApplication", descKey: "AdminRevisedApplicationDesc" },
      { key: "PROGRAM_APP_OWNER_NOTIFY", labelKey: "ProgramAppOwnerNotify", descKey: "ProgramAppOwnerNotifyDesc" },
    ],
  },
  {
    headingKey: "GroupRejection",
    prefs: [
      { key: "REJECTION_SOFT", labelKey: "RejectionSoft", descKey: "RejectionSoftDesc" },
      { key: "REJECTION_BUDGET", labelKey: "RejectionBudget", descKey: "RejectionBudgetDesc" },
      { key: "REJECTION_HARD_CRIT", labelKey: "RejectionHardCrit", descKey: "RejectionHardCritDesc" },
      { key: "REJECTION_GC_DECLINE", labelKey: "RejectionGcDecline", descKey: "RejectionGcDeclineDesc", requiresGrowthCoaches: true },
    ],
  },
  {
    headingKey: "GroupProgram",
    prefs: [
      { key: "PROGRAM_ENROLLMENT", labelKey: "ProgramEnrollment", descKey: "ProgramEnrollmentDesc" },
      { key: "PROGRAM_APP_ACCEPTED", labelKey: "ProgramAppAccepted", descKey: "ProgramAppAcceptedDesc" },
      { key: "PROGRAM_APP_REJECTED", labelKey: "ProgramAppRejected", descKey: "ProgramAppRejectedDesc" },
    ],
  },
  {
    headingKey: "GroupSow",
    requiresFullPortal: true,
    prefs: [
      { key: "SOW_FEEDBACK", labelKey: "SowFeedback", descKey: "SowFeedbackDesc" },
      { key: "SOW_KICKOFF", labelKey: "SowKickoff", descKey: "SowKickoffDesc" },
      { key: "SOW_SUBMITTED_FOUNDER", labelKey: "SowSubmittedFounder", descKey: "SowSubmittedFounderDesc" },
      { key: "SOW_SUBMITTED_IPE", labelKey: "SowSubmittedIpe", descKey: "SowSubmittedIpeDesc" },
      { key: "SOW_SUBMITTED_ADMIN", labelKey: "SowSubmittedAdmin", descKey: "SowSubmittedAdminDesc" },
      { key: "SOW_FULLY_SIGNED_FOUNDER", labelKey: "SowFullySignedFounder", descKey: "SowFullySignedFounderDesc" },
      { key: "SOW_FULLY_SIGNED_SP", labelKey: "SowFullySignedSp", descKey: "SowFullySignedSpDesc" },
      { key: "SOW_FULLY_SIGNED_ADMIN", labelKey: "SowFullySignedAdmin", descKey: "SowFullySignedAdminDesc" },
      { key: "SOW_CANCELLED_FOUNDER", labelKey: "SowCancelledFounder", descKey: "SowCancelledFounderDesc" },
      { key: "SOW_CANCELLED_SP", labelKey: "SowCancelledSp", descKey: "SowCancelledSpDesc" },
      { key: "SOW_TIER2_ADDED", labelKey: "SowTier2Added", descKey: "SowTier2AddedDesc" },
      { key: "SOW_TIER3_ADDED", labelKey: "SowTier3Added", descKey: "SowTier3AddedDesc" },
      { key: "IPE_SOW_APPROVED", labelKey: "IpeSowApproved", descKey: "IpeSowApprovedDesc" },
      { key: "IPE_SOW_FEEDBACK", labelKey: "IpeSowFeedback", descKey: "IpeSowFeedbackDesc" },
    ],
  },
  {
    headingKey: "GroupInvoicing",
    requiresFullPortal: true,
    prefs: [
      { key: "INVOICE_RETAINER_SUBMITTED", labelKey: "InvoiceRetainerSubmitted", descKey: "InvoiceRetainerSubmittedDesc" },
      { key: "INVOICE_RETAINER_REJECTED", labelKey: "InvoiceRetainerRejected", descKey: "InvoiceRetainerRejectedDesc" },
      { key: "INVOICE_FINAL_SUBMITTED", labelKey: "InvoiceFinalSubmitted", descKey: "InvoiceFinalSubmittedDesc" },
      { key: "INVOICE_APPROVED", labelKey: "InvoiceApproved", descKey: "InvoiceApprovedDesc" },
      { key: "INVOICE_FEEDBACK", labelKey: "InvoiceFeedback", descKey: "InvoiceFeedbackDesc" },
      { key: "INVOICE_RESUBMITTED", labelKey: "InvoiceResubmitted", descKey: "InvoiceResubmittedDesc" },
      { key: "SOW_FINAL_INVOICE_NOTICE", labelKey: "SowFinalInvoiceNotice", descKey: "SowFinalInvoiceNoticeDesc" },
      { key: "SOW_FINAL_INVOICE_REMINDER", labelKey: "SowFinalInvoiceReminder", descKey: "SowFinalInvoiceReminderDesc" },
    ],
  },
  {
    headingKey: "GroupIpe",
    requiresFullPortal: true,
    prefs: [
      { key: "IPE_ASSIGNMENT", labelKey: "IpeAssignment", descKey: "IpeAssignmentDesc" },
      { key: "IPE_REASSIGNMENT", labelKey: "IpeReassignment", descKey: "IpeReassignmentDesc" },
    ],
  },
];

/**
 * Org-level email notification toggles (Organization → Email Notifications).
 * Whole-region scope: a disabled template stops sending to every recipient in
 * this org's workflows. State renders straight from the suspense query (server
 * returns the fully-resolved registry map) and each flip PUTs a single-key
 * partial so sibling toggles are never clobbered.
 */
export default function OrgEmailNotificationSettings({ hasFullPortal, usesGrowthCoaches }: { hasFullPortal: boolean; usesGrowthCoaches: boolean }) {
  const { langState } = useContext(LangContext) as LangContextType;
  const t = textData.Pages.Settings.OrgEmailNotifications;

  const { data } = useSuspenseQuery({
    queryKey: queryKeys.regionSettings.orgEmailSettings(),
    queryFn: getOrgEmailSettings,
  });

  const updateSettings = useUpdateOrgEmailSettings(
    t.SaveSuccess[langState],
    t.SaveFail[langState]
  );

  const visibleGroups = TOGGLE_GROUPS.filter((g) => !g.requiresFullPortal || hasFullPortal)
    .map((g) => ({
      ...g,
      prefs: g.prefs.filter((p) => !p.requiresGrowthCoaches || usesGrowthCoaches)
    }))
    .filter((g) => g.prefs.length > 0);

  return (
    // pb (not mb): bottom margin on the last child is excluded from the scroll
    // container's scrollable extent, so the page ended flush at the last toggle
    <div className="self-baseline w-full flex flex-col gap-8 pb-16">
      <div className="-mb-2 flex flex-col gap-1">
        <p className="text-sm text-text/50">{t.Description[langState]}</p>
        <p className="text-sm text-text/50">{t.NonSuppressibleNote[langState]}</p>
      </div>

      {visibleGroups.map((group, groupIndex) => (
        <div
          key={group.headingKey}
          className={`flex flex-col gap-5 ${groupIndex > 0 ? "border-t border-text/10 pt-6" : ""}`}
        >
          <p className="text-base font-bold uppercase tracking-wider text-text/70">{t[group.headingKey][langState]}</p>
          {group.prefs.map(({ key, labelKey, descKey }) => (
            <div key={key}>
              <Toggle
                key={`${key}-${data.preferences[key]}`}
                label={t[labelKey][langState]}
                isDefaultChecked={data.preferences[key]}
                toggleActionCallback={(isChecked) => updateSettings.mutate({ [key]: isChecked })}
                checkedBg="bg-cSuccess"
                uncheckedBg="bg-cMediumGrey"
              />
              <p className="text-sm text-text/50 mt-1 ml-1">{t[descKey][langState]}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
